import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, MessageCircle, X } from "lucide-react";
import Logo from "./Logo";
import { PHONE_DISPLAY, PHONE_TEL, WHATSAPP_URL } from "@/lib/api";

const LINKS = [
  { href: "#calculator", label: "Calculator" },
  { href: "#servicii", label: "Servicii" },
  { href: "#galerie", label: "Galerie" },
  { href: "#proces", label: "Proces" },
  { href: "#intrebari", label: "Întrebări" },
  { href: "#contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <header className="fixed top-0 inset-x-0 z-50 border-b border-white/10 bg-hydro-navy/70 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
          <a href="#top" data-testid="nav-logo-link" aria-label="Puțul tău — acasă">
            <Logo />
          </a>
          <nav className="hidden lg:flex items-center gap-7">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.href.slice(1)}`}
                className="text-sm font-medium text-white/65 transition-colors hover:text-white"
              >
                {l.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <a
              href={PHONE_TEL}
              data-testid="nav-call-button"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-hydro-cyan px-5 py-2.5 text-sm font-bold text-hydro-navy transition-colors hover:bg-white"
            >
              <Phone size={15} strokeWidth={2.5} />
              {PHONE_DISPLAY}
            </a>
            <button
              type="button"
              data-testid="nav-menu-button"
              aria-label="Deschide meniul"
              onClick={() => setOpen(true)}
              className="lg:hidden grid h-10 w-10 place-items-center rounded-full border border-white/15 text-white"
            >
              <Menu size={20} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] flex flex-col bg-hydro-navy/95 backdrop-blur-xl p-6"
          >
            <div className="flex items-center justify-between">
              <Logo />
              <button
                type="button"
                data-testid="nav-close-button"
                aria-label="Închide meniul"
                onClick={() => setOpen(false)}
                className="grid h-10 w-10 place-items-center rounded-full border border-white/15 text-white"
              >
                <X size={20} />
              </button>
            </div>
            <nav className="mt-12 flex flex-col gap-5">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  data-testid={`drawer-link-${l.href.slice(1)}`}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, x: -18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="font-display text-4xl font-extrabold text-white/85 hover:text-hydro-cyan transition-colors"
                >
                  {l.label}
                </motion.a>
              ))}
            </nav>
            <div className="mt-auto grid grid-cols-2 gap-3">
              <a
                href={PHONE_TEL}
                data-testid="drawer-call-button"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-hydro-cyan px-5 py-4 text-base font-bold text-hydro-navy"
              >
                <Phone size={17} strokeWidth={2.5} />
                {PHONE_DISPLAY}
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="drawer-whatsapp-button"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-5 py-4 text-base font-bold text-white"
              >
                <MessageCircle size={17} />
                WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
